import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import api, { setAuthToken } from "utils/api";
import { queueAuthSuccessPopup } from "utils/authSuccessPopup";
import { setLogout, setUser } from "state";
import TeamShell from "scenes/team/TeamShell";
import { TeamLocaleProvider } from "scenes/team/TeamLocaleContext";
import EmployeeLogin from "./EmployeeLogin";
import AvailabilityManager from "./AvailabilityManager";
import "styles/booking.css";
import "styles/team.css";
import "styles/employee.css";

const isEmployeeUser = (user) =>
  user?.isEmployee === true || user?.isEmployee === "true";

const EmployeeDashboard = ({ user, onLogout }) => (
  <section className="team-page-inner">
    <header className="employee-topbar" aria-label="Mitarbeiter-Navigation">
      <Link to="/" className="team-topbar-brand" aria-label="Zur Website">
        <span className="team-topbar-mark" aria-hidden="true">
          <img src={`${process.env.PUBLIC_URL}/favicon.svg`} alt="" />
        </span>
        <span>Dr. Hoffmann</span>
      </Link>
      <div className="employee-topbar-user">
        {user.firstName ? `${user.firstName} ${user.lastName || ""}`.trim() : user.email}
      </div>
      <button type="button" className="team-topbar-logout" onClick={onLogout}>
        Abmelden
      </button>
    </header>

    <AvailabilityManager />
  </section>
);

const EmployeePageContent = () => {
  const token = useSelector((s) => s.token);
  const user = useSelector((s) => s.user);
  const dispatch = useDispatch();
  const [sessionReady, setSessionReady] = useState(!token);
  const [loginKey, setLoginKey] = useState(0);

  const refreshSession = useCallback(async () => {
    if (!token) {
      setAuthToken(null);
      setSessionReady(true);
      return;
    }
    setAuthToken(token);
    setSessionReady(false);
    try {
      const { data } = await api.get("/auth/me");
      dispatch(setUser({ user: data.user }));
    } catch {
      setAuthToken(null);
      dispatch(setLogout());
    } finally {
      setSessionReady(true);
    }
  }, [token, dispatch]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const handleLogout = () => {
    setAuthToken(null);
    dispatch(setLogout());
    queueAuthSuccessPopup("logout");
    setLoginKey((k) => k + 1);
  };

  if (!sessionReady) {
    return (
      <TeamShell>
        <div className="employee-empty">Lade…</div>
      </TeamShell>
    );
  }

  if (!token) {
    return (
      <TeamShell>
        <EmployeeLogin key={loginKey} onSuccess={refreshSession} />
      </TeamShell>
    );
  }

  if (!isEmployeeUser(user) || user?.isAdmin) {
    return (
      <TeamShell>
        <section className="team-login">
          <div className="auth-card team-login-card">
            <h1>Kein Zugriff</h1>
            <p className="auth-lead">
              Dieser Bereich ist nur für Mitarbeiter-Konten verfügbar.
            </p>
            <div className="auth-actions">
              <button type="button" className="auth-submit" onClick={handleLogout}>
                Abmelden
              </button>
            </div>
          </div>
        </section>
      </TeamShell>
    );
  }

  return (
    <TeamShell>
      <EmployeeDashboard user={user} onLogout={handleLogout} />
    </TeamShell>
  );
};

const EmployeePage = () => (
  <TeamLocaleProvider>
    <EmployeePageContent />
  </TeamLocaleProvider>
);

export default EmployeePage;
